/**
 * How a credit note value was calculated / where it came from.
 * Dummy data until backend exposes GET /store/credit_notes/:id/origin.
 */

/**
 * @typedef {Object} CreditNoteOriginLine
 * @property {string} label
 * @property {number|null} [amount]
 * @property {number|null} [percent]
 * @property {boolean} [emphasis]
 * @property {boolean} [is_total]
 */

/**
 * @typedef {Object} CreditNoteOriginCalculation
 * @property {number} credit_note_id
 * @property {string} identifier
 * @property {number} credit_value
 * @property {string} source_type
 * @property {string} source_label
 * @property {string} origin_summary
 * @property {string|null} [reference_order_name]
 * @property {number|null} [reference_order_id]
 * @property {string|null} [issued_at]
 * @property {CreditNoteOriginLine[]} calculation_lines
 */

const MOCK_ORIGIN_BY_CREDIT_NOTE_ID = {
  1: {
    source_type: "order_return",
    source_label: "Order return credit",
    origin_summary:
      "Credit issued after partial return on Ravi Kitchen. Eligible return value was applied at 10% of the returned line items.",
    reference_order_name: "Ravi Kitchen — Koramangala",
    reference_order_id: 501,
    issued_at: "2026-05-12T10:30:00.000Z",
    calculation_lines: [
      { label: "Returned items value (ex-GST)", amount: 85000 },
      { label: "Return handling fee", amount: -2500 },
      { label: "Eligible credit rate", percent: 12 },
      { label: "Credit issued", amount: 10000, is_total: true, emphasis: true },
    ],
  },
  2: {
    source_type: "promotion",
    source_label: "Dealer promotion",
    origin_summary:
      "Promotional credit for meeting Q1 volume target. Not tied to a single order.",
    reference_order_name: null,
    reference_order_id: null,
    issued_at: "2026-05-01T09:00:00.000Z",
    calculation_lines: [
      { label: "Target order value (Q1)", amount: 500000 },
      { label: "Promotion tier credit", percent: 1 },
      { label: "Credit issued", amount: 5000, is_total: true, emphasis: true },
    ],
  },
  4: {
    source_type: "service_adjustment",
    source_label: "Service adjustment",
    origin_summary:
      "Goodwill credit after delivery delay on Sunrise Office Fit-out.",
    reference_order_name: "Sunrise Office Fit-out",
    reference_order_id: 502,
    issued_at: "2026-05-04T14:00:00.000Z",
    calculation_lines: [
      { label: "Order value", amount: 287800 },
      { label: "Delay compensation (fixed)", amount: 15000 },
      { label: "Credit issued", amount: 15000, is_total: true, emphasis: true },
    ],
  },
  101: {
    source_type: "order_return",
    source_label: "Order return credit",
    origin_summary:
      "Credit from wardrobe scope reduction on Priya Wardrobe order.",
    reference_order_name: "Priya Wardrobe — Master Bed",
    reference_order_id: 601,
    issued_at: "2026-05-09T11:00:00.000Z",
    calculation_lines: [
      { label: "Revised scope value", amount: 62000 },
      { label: "Original quoted value", amount: 68900 },
      { label: "Difference credited", amount: 5000, is_total: true, emphasis: true },
    ],
  },
};

const DEFAULT_ORIGIN = {
  source_type: "manual",
  source_label: "Manual credit note",
  origin_summary:
    "Credit note issued manually by operations. Calculation details will appear here when linked to a source order or policy.",
  reference_order_name: null,
  reference_order_id: null,
  issued_at: null,
  calculation_lines: [
    { label: "Approved credit amount", amount: null },
    { label: "Credit issued", amount: null, is_total: true, emphasis: true },
  ],
};

/**
 * Returns dummy origin / calculation breakdown for a credit note.
 * Replace with API call when backend is ready.
 *
 * @param {{ id: number|string, identifier?: string, value?: number }} creditNote
 * @returns {CreditNoteOriginCalculation}
 */
export function getCreditNoteOriginCalculationDummy(creditNote) {
  const id = Number(creditNote?.id);
  const template = MOCK_ORIGIN_BY_CREDIT_NOTE_ID[id] ?? DEFAULT_ORIGIN;
  const creditValue = Number(creditNote?.value) || 0;

  const calculation_lines = template.calculation_lines.map((line) => {
    if (line.is_total) {
      return { ...line, amount: creditValue };
    }
    return { ...line };
  });

  return {
    credit_note_id: id,
    identifier: String(creditNote?.identifier || ""),
    credit_value: creditValue,
    ...template,
    calculation_lines,
  };
}
